import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Employee } from './types/Employee';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

/**
 * Generate Employee Data PDF for Tax Advisor
 * Contains employee master data for registration purposes
 * No employer information, no hours calculation, no payments
 */
export const generateEmployeeDataPDF = (employee: Employee) => {
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE');
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const docDefinition: any = {
    content: [
      {
        text: 'Mitarbeiterdaten',
        style: 'header',
        margin: [0, 0, 0, 20]
      },
      {
        text: 'Datenblatt für Anmeldung',
        style: 'subheader',
        margin: [0, 0, 0, 30]
      },

      // Personal Information
      {
        text: 'Persönliche Daten',
        style: 'sectionHeader',
        margin: [0, 10, 0, 10]
      },
      {
        table: {
          widths: [150, '*'],
          body: [
            ['Vorname:', formatValue(employee.first_name)],
            ['Nachname:', formatValue(employee.last_name)],
            ['E-Mail:', formatValue(employee.EMail)],
            ['Mobiltelefon:', formatValue(employee.mobile_phone)],
            ['Telefon (2):', formatValue(employee.secondary_phone)]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20]
      },

      // Address
      {
        text: 'Anschrift',
        style: 'sectionHeader',
        margin: [0, 10, 0, 10]
      },
      {
        table: {
          widths: [150, '*'],
          body: [
            ['Straße, Hausnummer:', formatValue(employee.street)],
            ['Postleitzahl:', formatValue(employee.postal_code)],
            ['Ort:', formatValue(employee.city)]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20]
      },

      // Bank Account
      {
        text: 'Bankverbindung',
        style: 'sectionHeader',
        margin: [0, 10, 0, 10]
      },
      {
        table: {
          widths: [150, '*'],
          body: [
            ['Kontoinhaber:', formatValue(employee.account_holder)],
            ['IBAN:', formatValue(employee.iban)],
            ['BIC:', formatValue(employee.bic)],
            ['Bank:', formatValue(employee.bank_name)]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20]
      },

      // Social Security & Insurance
      {
        text: 'Sozialversicherung',
        style: 'sectionHeader',
        margin: [0, 10, 0, 10]
      },
      {
        table: {
          widths: [150, '*'],
          body: [
            ['Sozialversicherungsnummer:', formatValue(employee.social_security_number)],
            ['Krankenkasse:', formatValue(employee.health_insurance)]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20]
      },

      // Employment Details
      {
        text: 'Beschäftigungsdaten',
        style: 'sectionHeader',
        margin: [0, 10, 0, 10]
      },
      {
        table: {
          widths: [150, '*'],
          body: [
            ['Beschäftigungsbeginn:', formatDate(employee.employment_start_date)],
            ['Beschäftigungsende:', formatDate(employee.employment_end_date)],
            ['Urlaubstage pro Jahr:', formatValue(employee.vacation_days_per_year) + ' Tage'],
            ['Vertragliche Stunden/Monat:', formatValue(employee.contracted_hours_per_month) + ' Std.'],
            ['Stundensatz:', formatValue(employee.hourly_rate) + ' €']
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20]
      },

      // Footer Note
      {
        text: 'Dieses Dokument enthält die Mitarbeiterstammdaten für Anmeldezwecke. Stundenabrechnung und Zahlungen erfolgen über separate Buchhaltungssoftware.',
        style: 'footerNote',
        margin: [0, 30, 0, 0]
      },
      {
        text: `Erstellt am: ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')}`,
        style: 'footerNote',
        margin: [0, 10, 0, 0]
      }
    ],
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    styles: {
      header: {
        fontSize: 20,
        bold: true,
        color: '#333'
      },
      subheader: {
        fontSize: 14,
        color: '#666'
      },
      sectionHeader: {
        fontSize: 14,
        bold: true,
        color: '#000',
        fillColor: '#f0f0f0',
        margin: [0, 5, 0, 5]
      },
      footerNote: {
        fontSize: 9,
        color: '#999',
        italics: true
      }
    }
  };

  const fileName = `Mitarbeiterdaten_${employee.last_name}_${employee.first_name}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdfMake.createPdf(docDefinition).download(fileName);
};
